import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import { FinancialConfig, calculateDivision293Detail } from './FinancialConfig.js';
import { deriveDivision293DefaultsFromSmsfState } from './division296FundEarnings.js';
import { SMSFExportEngine } from './SMSFExportEngine';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import CalculationTransparency from './components/CalculationTransparency.js';

const SMSFDivision293Calculator = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('calculator');
    
    // Division 293 State
    const [taxData, setTaxData] = useState(() => {
        const fromState = deriveDivision293DefaultsFromSmsfState(smsfState);
        const defaultCc = fromState.concessionalContributions
            ?? SMSF_RATES_CONFIG.contributionCaps.concessional.limit * 0.917;
        return {
            assessableIncome: 200000,
            reportableFringeBenefits: 0,
            reportableEmployerSuper: 0,
            concessionalContributions: defaultCc,
            netInvestmentLoss: 0,
            capitalGains: 0,
            taxableIncome: 200000,
        };
    });

    const [paymentOption, setPaymentOption] = useState('personal'); // personal, release_authority, defer
    const [releasePercentage, setReleasePercentage] = useState(100);
    const [deferYears, setDeferYears] = useState(0);

    // Division 293 Constants (2024-25)
    const DIV293_THRESHOLD = FinancialConfig.DIVISION_293.INCOME_THRESHOLD;
    const DIV293_RATE = FinancialConfig.DIVISION_293.TAX_RATE;
    const RELEASE_AUTHORITY_MAX = 100;

    useEffect(() => {
        const fromState = deriveDivision293DefaultsFromSmsfState(smsfState);
        if (!fromState.concessionalContributions) return;
        setTaxData((prev) => ({
            ...prev,
            concessionalContributions: fromState.concessionalContributions,
        }));
    }, [smsfState]);

    // Calculate income for surcharge purposes
    const calculateIncomeForSurchargePurposes = () => {
        const {
            assessableIncome,
            reportableFringeBenefits,
            reportableEmployerSuper,
            netInvestmentLoss,
            capitalGains
        } = taxData;

        // Income for surcharge purposes formula
        const income = 
            assessableIncome + 
            reportableFringeBenefits + 
            reportableEmployerSuper - 
            netInvestmentLoss;

        return Math.max(0, income);
    };

    // Calculate Division 293 tax liability (FinancialConfig SSOT)
    const calculateDivision293Tax = () => {
        const incomeForSurcharge = calculateIncomeForSurchargePurposes();
        const { concessionalContributions } = taxData;
        const detail = calculateDivision293Detail(incomeForSurcharge, concessionalContributions);
        return {
            ...detail,
            isExempt: detail.isExempt,
        };
    };

    // Calculate payment options
    const calculatePaymentOptions = () => {
        const div293 = calculateDivision293Tax();
        const { div293Tax } = div293;

        // Option 1: Pay personally
        const personalPayment = {
            amount: div293Tax,
            description: 'Pay from personal funds',
            impact: 'No impact on super balance',
            cashFlow: -div293Tax,
            superBalance: 0
        };

        // Option 2: Release authority
        const releaseAmount = (div293Tax * releasePercentage) / 100;
        const releaseAuthority = {
            amount: releaseAmount,
            releasePercentage,
            description: 'Release from super fund',
            impact: 'Reduces super balance',
            cashFlow: 0,
            superBalance: -releaseAmount,
            remainingToPay: div293Tax - releaseAmount
        };

        // Option 3: Defer payment (with interest)
        const interestRate = 0.08; // Approximate GIC rate
        const deferredAmount = div293Tax * Math.pow(1 + interestRate, deferYears);
        const deferPayment = {
            amount: div293Tax,
            deferYears,
            futureAmount: deferredAmount,
            interestCost: deferredAmount - div293Tax,
            description: 'Defer until debt raised',
            impact: 'Interest charges apply',
            cashFlow: 0,
            superBalance: 0
        };

        return {
            personalPayment,
            releaseAuthority,
            deferPayment
        };
    };

    // Generate multi-year projection
    const generateProjection = () => {
        const projections = [];
        const currentYear = new Date().getFullYear();

        for (let year = 0; year < 5; year++) {
            // Assume income growth of 3% per year
            const projectedIncome = taxData.assessableIncome * Math.pow(1.03, year);
            const projectedContributions = taxData.concessionalContributions;
            
            const projectedIncomeForSurcharge = 
                projectedIncome + 
                taxData.reportableFringeBenefits + 
                taxData.reportableEmployerSuper - 
                taxData.netInvestmentLoss;

            const detail = calculateDivision293Detail(projectedIncomeForSurcharge, projectedContributions);

            projections.push({
                financialYear: `${currentYear + year}-${(currentYear + year + 1).toString().slice(-2)}`,
                income: projectedIncome,
                incomeForSurcharge: projectedIncomeForSurcharge,
                contributions: projectedContributions,
                excessIncome: detail.excessIncome,
                taxableAmount: detail.taxableAmount,
                div293Tax: detail.div293Tax,
                isLiable: detail.div293Tax > 0
            });
        }

        return projections;
    };

    // Calculate strategies to reduce Division 293 tax
    const calculateReductionStrategies = () => {
        const div293 = calculateDivision293Tax();
        const strategies = [];

        // Strategy 1: Reduce concessional contributions
        if (div293.div293Tax > 0) {
            const targetContributions = Math.max(0, 
                taxData.concessionalContributions - div293.taxableAmount
            );
            const savings = div293.div293Tax;
            
            strategies.push({
                strategy: 'Reduce Concessional Contributions',
                description: `Lower contributions to $${targetContributions.toLocaleString()}`,
                currentTax: div293.div293Tax,
                newTax: 0,
                savings,
                feasibility: 'High',
                considerations: 'Reduces retirement savings but eliminates Division 293 tax'
            });
        }

        // Strategy 2: Salary sacrifice to reduce taxable income
        const excessIncome = div293.excessIncome;
        if (excessIncome > 0 && excessIncome < taxData.concessionalContributions) {
            const additionalSalSac = Math.min(excessIncome, SMSF_RATES_CONFIG.contributionCaps.concessional.limit - taxData.concessionalContributions);
            if (additionalSalSac > 0) {
                strategies.push({
                    strategy: 'Increase Salary Sacrifice',
                    description: `Salary sacrifice additional $${additionalSalSac.toLocaleString()}`,
                    currentTax: div293.div293Tax,
                    newTax: div293.div293Tax * 0.5, // Approximate reduction
                    savings: div293.div293Tax * 0.5,
                    feasibility: 'Medium',
                    considerations: 'Reduces take-home pay but may lower overall tax burden'
                });
            }
        }

        // Strategy 3: Spouse contributions
        if (div293.div293Tax > 0) {
            const spouseContribution = Math.min(3000, div293.taxableAmount);
            strategies.push({
                strategy: 'Spouse Contributions',
                description: `Contribute $${spouseContribution.toLocaleString()} to spouse super`,
                currentTax: div293.div293Tax,
                newTax: div293.div293Tax,
                savings: spouseContribution * 0.18, // Tax offset
                feasibility: 'Medium',
                considerations: 'Eligible for tax offset, but doesn\'t reduce Division 293 directly'
            });
        }

        // Strategy 4: Timing of contributions
        if (div293.excessIncome < 10000) {
            strategies.push({
                strategy: 'Defer Contributions',
                description: 'Delay contributions to next financial year',
                currentTax: div293.div293Tax,
                newTax: 0,
                savings: div293.div293Tax,
                feasibility: 'High',
                considerations: 'Only viable if income likely to drop below threshold'
            });
        }

        return strategies;
    };

    const div293Results = calculateDivision293Tax();
    const paymentOptions = calculatePaymentOptions();
    const projections = generateProjection();
    const strategies = calculateReductionStrategies();

    const renderCalculator = () => (
        <div className="space-y-6">
            {/* Division 293 Status */}
            <div className={`p-4 rounded-lg border-2 ${
                div293Results.isExempt ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'
            }`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold">
                            {div293Results.isExempt ? '✅ No Division 293 Tax Liability' : '⚠️ Division 293 Tax Applicable'}
                        </h3>
                        <p className="text-sm text-gray-600">
                            {div293Results.isExempt 
                                ? `Income for surcharge purposes ($${div293Results.incomeForSurcharge.toLocaleString()}) is below threshold`
                                : `Income exceeds threshold by $${div293Results.excessIncome.toLocaleString()}`
                            }
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-3xl font-bold text-red-600">
                            ${div293Results.div293Tax.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600">Division 293 Tax</div>
                    </div>
                </div>
            </div>

            <CalculationTransparency
                kind="smsf"
                title="Division 293 Transparency"
                inputs={{
                    currentIncome: div293Results.incomeForSurcharge,
                    contributionAmount: taxData.concessionalContributions,
                }}
                results={{
                    ...div293Results,
                    tableData: projections,
                    recommendations: strategies,
                    warnings: div293Results.div293Tax > 0 ? [{ message: 'Division 293 tax applies on these inputs.' }] : [],
                }}
            />

            {/* Income Inputs */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Income Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assessable Income ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={taxData.assessableIncome}
                            onChange={(e) => setTaxData({ ...taxData, assessableIncome: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="text-xs text-gray-500 mt-1">Salary, wages, business income, etc.</div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reportable Fringe Benefits ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={taxData.reportableFringeBenefits || ''}
                            onChange={(e) => setTaxData({ ...taxData, reportableFringeBenefits: Number(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="text-xs text-gray-500 mt-1">From payment summary</div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reportable Employer Super Contributions ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={taxData.reportableEmployerSuper || ''}
                            onChange={(e) => setTaxData({ ...taxData, reportableEmployerSuper: Number(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="text-xs text-gray-500 mt-1">Salary sacrifice amounts</div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Net Investment Loss ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={taxData.netInvestmentLoss || ''}
                            onChange={(e) => setTaxData({ ...taxData, netInvestmentLoss: Number(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="text-xs text-gray-500 mt-1">Negative gearing, etc.</div>
                    </div>
                </div>
            </div>

            {/* Contributions */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Concessional Contributions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Total Concessional Contributions ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={taxData.concessionalContributions}
                            onChange={(e) => setTaxData({ ...taxData, concessionalContributions: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                            Employer + salary sacrifice (max ${FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL.toLocaleString()})
                        </div>
                    </div>
                    <div className="flex items-center">
                        <div className="p-4 bg-blue-50 rounded-lg w-full">
                            <div className="text-sm text-gray-600">Contributions Subject to Div 293</div>
                            <div className="text-2xl font-bold text-blue-600">
                                ${div293Results.taxableAmount.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {div293Results.marginalRate.toFixed(1)}% of total contributions
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Calculation Breakdown */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Calculation Breakdown</h3>
                <div className="space-y-3">
                    {[
                        { label: 'Assessable Income', value: taxData.assessableIncome },
                        { label: '+ Reportable Fringe Benefits', value: taxData.reportableFringeBenefits },
                        { label: '+ Reportable Employer Super', value: taxData.reportableEmployerSuper },
                        { label: '- Net Investment Loss', value: -taxData.netInvestmentLoss },
                        { label: 'Income for Surcharge Purposes', value: div293Results.incomeForSurcharge, isSubtotal: true },
                        { label: '- Division 293 Threshold', value: -DIV293_THRESHOLD },
                        { label: 'Excess Income', value: div293Results.excessIncome, isSubtotal: true },
                        { label: 'Concessional Contributions', value: taxData.concessionalContributions },
                        { label: 'Taxable Amount (Lesser of Excess or Contributions)', value: div293Results.taxableAmount, highlight: true },
                        { label: 'Division 293 Tax (15% of Taxable Amount)', value: div293Results.div293Tax, isTotal: true }
                    ].map((item, index) => (
                        <div key={index} className={`flex justify-between py-2 ${
                            item.isTotal ? 'border-t-2 border-gray-300 pt-3 mt-3' :
                            item.isSubtotal ? 'border-t border-gray-200 pt-2' :
                            'border-b border-gray-100'
                        }`}>
                            <span className={`${
                                item.isTotal ? 'font-bold text-gray-800 text-lg' :
                                item.highlight ? 'font-semibold text-blue-700' :
                                item.isSubtotal ? 'font-semibold text-gray-700' :
                                'text-gray-600'
                            }`}>
                                {item.label}:
                            </span>
                            <span className={`${
                                item.isTotal ? 'font-bold text-red-600 text-xl' :
                                item.highlight ? 'font-semibold text-blue-600 text-lg' :
                                item.isSubtotal ? 'font-semibold' :
                                'font-medium'
                            }`}>
                                ${Math.abs(item.value).toLocaleString()}
                                {item.value < 0 && item.label.includes('-') ? '' : ''}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderPaymentOptions = () => (
        <div className="space-y-6">
            {/* Tax Summary */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Division 293 Tax Liability</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                        <div className="text-3xl font-bold text-red-600">
                            ${div293Results.div293Tax.toLocaleString()}
                        </div>
                        <div className="text-sm text-red-700">Total Tax Due</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <div className="text-3xl font-bold text-blue-600">
                            {div293Results.effectiveTaxRate.toFixed(1)}%
                        </div>
                        <div className="text-sm text-blue-700">Effective Tax Rate</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <div className="text-3xl font-bold text-purple-600">
                            ${div293Results.taxableAmount.toLocaleString()}
                        </div>
                        <div className="text-sm text-purple-700">Taxable Amount</div>
                    </div>
                </div>
            </div>

            {/* Payment Option Selector */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Select Payment Method</h3>
                <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                        <input
                            type="radio"
                            id="personal"
                            value="personal"
                            checked={paymentOption === 'personal'}
                            onChange={(e) => setPaymentOption(e.target.value)}
                            className="h-4 w-4 text-blue-600"
                        />
                        <label htmlFor="personal" className="flex-1">
                            <div className="font-semibold">Pay Personally</div>
                            <div className="text-sm text-gray-600">Pay from your own funds without touching super</div>
                        </label>
                    </div>
                    <div className="flex items-center space-x-4">
                        <input
                            type="radio"
                            id="release"
                            value="release_authority"
                            checked={paymentOption === 'release_authority'}
                            onChange={(e) => setPaymentOption(e.target.value)}
                            className="h-4 w-4 text-blue-600"
                        />
                        <label htmlFor="release" className="flex-1">
                            <div className="font-semibold">Release Authority</div>
                            <div className="text-sm text-gray-600">Request ATO to release funds from your super</div>
                        </label>
                    </div>
                    <div className="flex items-center space-x-4">
                        <input
                            type="radio"
                            id="defer"
                            value="defer"
                            checked={paymentOption === 'defer'}
                            onChange={(e) => setPaymentOption(e.target.value)}
                            className="h-4 w-4 text-blue-600"
                        />
                        <label htmlFor="defer" className="flex-1">
                            <div className="font-semibold">Defer Payment</div>
                            <div className="text-sm text-gray-600">Pay later when debt is raised (interest charges apply)</div>
                        </label>
                    </div>
                </div>
            </div>

            {/* Payment Option Details */}
            {paymentOption === 'personal' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Personal Payment Details</h3>
                    <div className="space-y-4">
                        <div className="p-4 bg-green-50 rounded-lg">
                            <h4 className="font-semibold text-green-800 mb-2">Benefits</h4>
                            <ul className="text-sm text-green-700 space-y-1">
                                <li>• Preserves full super balance</li>
                                <li>• No reduction in retirement savings</li>
                                <li>• Simple and straightforward</li>
                                <li>• No ongoing interest or debt</li>
                            </ul>
                        </div>
                        <div className="p-4 bg-blue-50 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-2">Payment Required</h4>
                            <div className="text-3xl font-bold text-blue-600">
                                ${paymentOptions.personalPayment.amount.toLocaleString()}
                            </div>
                            <p className="text-sm text-blue-700 mt-2">
                                Due when ATO assessment notice is issued (typically within 12 months)
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {paymentOption === 'release_authority' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Release Authority Details</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Release Percentage (%)
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={releasePercentage}
                                onChange={(e) => setReleasePercentage(Number(e.target.value))}
                                className="w-full"
                            />
                            <div className="flex justify-between text-sm text-gray-600">
                                <span>0%</span>
                                <span className="font-semibold">{releasePercentage}%</span>
                                <span>100%</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-red-50 rounded-lg">
                                <div className="text-sm text-gray-600">Amount Released from Super</div>
                                <div className="text-2xl font-bold text-red-600">
                                    ${paymentOptions.releaseAuthority.amount.toLocaleString()}
                                </div>
                            </div>
                            <div className="p-4 bg-blue-50 rounded-lg">
                                <div className="text-sm text-gray-600">Remaining to Pay Personally</div>
                                <div className="text-2xl font-bold text-blue-600">
                                    ${paymentOptions.releaseAuthority.remainingToPay.toLocaleString()}
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-yellow-50 rounded-lg">
                            <h4 className="font-semibold text-yellow-800 mb-2">Important Considerations</h4>
                            <ul className="text-sm text-yellow-700 space-y-1">
                                <li>• Reduces your super balance and retirement savings</li>
                                <li>• Can be split between super and personal payment</li>
                                <li>• Fund must release within 10 business days</li>
                                <li>• Cannot be reversed once released</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {paymentOption === 'defer' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Deferred Payment Details</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Defer for (years)
                            </label>
                            <input
                                {...getNumericInputProps()}
                                min="0"
                                max="10"
                                value={deferYears || ''}
                                onChange={(e) => setDeferYears(Number(e.target.value) || 0)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 bg-blue-50 rounded-lg text-center">
                                <div className="text-sm text-gray-600">Original Amount</div>
                                <div className="text-2xl font-bold text-blue-600">
                                    ${paymentOptions.deferPayment.amount.toLocaleString()}
                                </div>
                            </div>
                            <div className="p-4 bg-red-50 rounded-lg text-center">
                                <div className="text-sm text-gray-600">Interest Charges</div>
                                <div className="text-2xl font-bold text-red-600">
                                    ${paymentOptions.deferPayment.interestCost.toLocaleString()}
                                </div>
                            </div>
                            <div className="p-4 bg-purple-50 rounded-lg text-center">
                                <div className="text-sm text-gray-600">Total to Pay</div>
                                <div className="text-2xl font-bold text-purple-600">
                                    ${paymentOptions.deferPayment.futureAmount.toLocaleString()}
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-red-50 rounded-lg">
                            <h4 className="font-semibold text-red-800 mb-2">Warning</h4>
                            <ul className="text-sm text-red-700 space-y-1">
                                <li>• Interest charges accrue at General Interest Charge (GIC) rate (~8% p.a.)</li>
                                <li>• Debt may be raised if you have access to super (retirement/preservation age)</li>
                                <li>• Total amount increases significantly over time</li>
                                <li>• Not recommended unless you have specific cash flow issues</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const renderProjections = () => (
        <div className="space-y-6">
            {/* 5-Year Projection */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">5-Year Division 293 Projection</h3>
                <p className="text-sm text-gray-600 mb-4">
                    Assumes 3% annual income growth and constant contribution levels
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4">Financial Year</th>
                                <th className="text-right py-3 px-4">Income</th>
                                <th className="text-right py-3 px-4">Income for Surcharge</th>
                                <th className="text-right py-3 px-4">Contributions</th>
                                <th className="text-right py-3 px-4">Excess Income</th>
                                <th className="text-right py-3 px-4">Taxable Amount</th>
                                <th className="text-right py-3 px-4">Div 293 Tax</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projections.map((proj, index) => (
                                <tr key={index} className={`border-b border-gray-100 ${
                                    index === 0 ? 'bg-blue-50' : ''
                                } ${proj.isLiable ? 'bg-red-50' : ''}`}>
                                    <td className="py-3 px-4 font-medium">{proj.financialYear}</td>
                                    <td className="py-3 px-4 text-right">${proj.income.toLocaleString()}</td>
                                    <td className="py-3 px-4 text-right">${proj.incomeForSurcharge.toLocaleString()}</td>
                                    <td className="py-3 px-4 text-right">${proj.contributions.toLocaleString()}</td>
                                    <td className="py-3 px-4 text-right">
                                        {proj.excessIncome > 0 ? `$${proj.excessIncome.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        {proj.taxableAmount > 0 ? `$${proj.taxableAmount.toLocaleString()}` : '-'}
                                    </td>
                                    <td className={`py-3 px-4 text-right font-semibold ${
                                        proj.div293Tax > 0 ? 'text-red-600' : 'text-green-600'
                                    }`}>
                                        {proj.div293Tax > 0 ? `$${proj.div293Tax.toLocaleString()}` : 'Nil'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-gray-300 bg-gray-50">
                                <td colSpan="6" className="py-3 px-4 text-right font-bold">Total 5-Year Liability:</td>
                                <td className="py-3 px-4 text-right font-bold text-red-600">
                                    ${projections.reduce((sum, p) => sum + p.div293Tax, 0).toLocaleString()}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* Cumulative Impact */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Cumulative Impact on Super</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <div className="text-sm text-gray-600">5-Year Contributions</div>
                        <div className="text-2xl font-bold text-blue-600">
                            ${(taxData.concessionalContributions * 5).toLocaleString()}
                        </div>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                        <div className="text-sm text-gray-600">5-Year Div 293 Tax</div>
                        <div className="text-2xl font-bold text-red-600">
                            ${projections.reduce((sum, p) => sum + p.div293Tax, 0).toLocaleString()}
                        </div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                        <div className="text-sm text-gray-600">Net to Super</div>
                        <div className="text-2xl font-bold text-green-600">
                            ${((taxData.concessionalContributions * 5) - projections.reduce((sum, p) => sum + p.div293Tax, 0)).toLocaleString()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStrategies = () => (
        <div className="space-y-6">
            {/* Reduction Strategies */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Tax Reduction Strategies</h3>
                {strategies.length > 0 ? (
                    <div className="space-y-4">
                        {strategies.map((strategy, index) => (
                            <div key={index} className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r-lg">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h4 className="font-semibold text-blue-800">{strategy.strategy}</h4>
                                        <p className="text-sm text-blue-700">{strategy.description}</p>
                                    </div>
                                    <span className={`px-2 py-1 text-xs rounded ml-4 ${
                                        strategy.feasibility === 'High' ? 'bg-green-100 text-green-800' :
                                        strategy.feasibility === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                    }`}>
                                        {strategy.feasibility} Feasibility
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-4 mt-3">
                                    <div>
                                        <div className="text-xs text-gray-600">Current Tax</div>
                                        <div className="font-semibold text-red-600">${strategy.currentTax.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600">New Tax</div>
                                        <div className="font-semibold text-blue-600">${strategy.newTax.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600">Annual Savings</div>
                                        <div className="font-semibold text-green-600">${strategy.savings.toLocaleString()}</div>
                                    </div>
                                </div>
                                <p className="text-sm text-blue-600 mt-2">
                                    <strong>Considerations:</strong> {strategy.considerations}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-green-700">No Division 293 tax liability - no strategies needed</p>
                    </div>
                )}
            </div>

            {/* General Strategies */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">General Planning Strategies</h3>
                <div className="space-y-4">
                    {[
                        {
                            title: 'Monitor Income Threshold',
                            description: 'Track your income for surcharge purposes throughout the year',
                            tips: ['Review payslips regularly', 'Consider timing of bonuses', 'Watch for changes in FBT']
                        },
                        {
                            title: 'Optimize Contribution Timing',
                            description: 'Time contributions to minimize Division 293 tax',
                            tips: ['Contribute after high-income years', 'Use spouse contributions strategically', 'Consider bring-forward rule']
                        },
                        {
                            title: 'Non-Concessional Contributions',
                            description: 'Use after-tax contributions to avoid Division 293',
                            tips: [`No Division 293 on non-concessional`, `Annual cap of $${FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL.toLocaleString()}`, `Bring-forward up to $${FinancialConfig.BRING_FORWARD_RULE.MAXIMUM_CONTRIBUTION.toLocaleString()}`]
                        },
                        {
                            title: 'Investment Strategy',
                            description: 'Minimize reportable fringe benefits and other income',
                            tips: ['Review salary packaging', 'Consider investment structure', 'Manage capital gains timing']
                        }
                    ].map((item, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-lg">
                            <h4 className="font-semibold text-gray-800 mb-2">{item.title}</h4>
                            <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                            <ul className="text-sm text-gray-700 space-y-1">
                                {item.tips.map((tip, tipIndex) => (
                                    <li key={tipIndex}>• {tip}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Division 293 Tax Calculator</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('calculator')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'calculator' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Calculator
                    </button>
                    <button
                        onClick={() => setActiveTab('payment')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'payment' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Payment Options
                    </button>
                    <button
                        onClick={() => setActiveTab('projections')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'projections' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Projections
                    </button>
                    <button
                        onClick={() => setActiveTab('strategies')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'strategies' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Strategies
                    </button>
                </div>
            </div>

            {/* Content based on active tab */}
            {activeTab === 'calculator' && renderCalculator()}
            {activeTab === 'payment' && renderPaymentOptions()}
            {activeTab === 'projections' && renderProjections()}
            {activeTab === 'strategies' && renderStrategies()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Division 293 Analysis as of: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button 
                            onClick={() => SMSFExportEngine.exportToPDF('division293', {
                                ...taxData,
                                fundName: smsfState.smsfData?.fundName
                            })}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                            Export Division 293 Report (PDF)
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Save Calculation
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

SMSFDivision293Calculator.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFDivision293Calculator;